import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Mic, MicOff, Volume2, Loader2, RotateCcw, Phone, Brain, FolderKanban, Building, User } from 'lucide-react';
import { ScopeCombobox } from '../components/scope/ScopeCombobox';
import { AsyncScopeCombobox } from '../components/scope/AsyncScopeCombobox';
import { LanguageCombobox } from '../components/voice/LanguageCombobox';
import { VoiceUpsellDialog } from '../components/voice/VoiceUpsellDialog';
import { hasVoiceStandard } from '../lib/voice-access';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function VoiceBubble({ role, text, isPlaying }) {
  return (
    <div className={`flex ${role === 'user' ? 'justify-end' : 'justify-start'} mb-4`} data-testid={`voice-bubble-${role}`}>
      <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
        role === 'user'
          ? 'bg-primary text-primary-foreground rounded-br-md'
          : 'bg-muted rounded-bl-md'
      }`}>
        <p>{text}</p>
        {role === 'assistant' && isPlaying && (
          <div className="flex items-center gap-1.5 mt-2 text-xs opacity-70">
            <Volume2 className="w-3 h-3 animate-pulse" />
            <span>Lecture en cours...</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function VoiceChatPage() {
  const { personId } = useParams();
  const navigate = useNavigate();
  const { token, user, tenant } = useAuth();

  const [person, setPerson] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [upsellOpen, setUpsellOpen] = useState(false);

  // Check feature access (tenant + user level)
  const features = tenant?.features || {};
  const hasVoiceAccess = hasVoiceStandard(user, tenant);

  // Scope state — identical to CloneChatPage
  const [scope, setScope] = useState(null);
  const [scopeId, setScopeId] = useState(null);
  const [scopeEntities, setScopeEntities] = useState({ dossiers: [], organizations: [], contacts: [] });

  // Refs to avoid stale closures in MediaRecorder callbacks
  const scopeRef = useRef(null);
  const scopeIdRef = useRef(null);

  // Language selector state
  const [voiceLang, setVoiceLang] = useState('auto');
  const voiceLangRef = useRef('auto');

  const handleLangChange = (val) => {
    setVoiceLang(val);
    voiceLangRef.current = val;
  };

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const sessionIdRef = useRef(crypto.randomUUID());
  const audioRef = useRef(null);
  const messagesEndRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    fetchPerson();
    fetchScopeEntities();
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, [personId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchPerson = async () => {
    try {
      const res = await axios.get(`${API}/persons/${personId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPerson(res.data);
    } catch {
      toast.error('Clone introuvable');
      navigate('/clones');
    }
  };

  const fetchScopeEntities = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [d, o, c] = await Promise.all([
        axios.get(`${API}/dossiers`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/organizations`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/contacts?limit=500`, { headers }).catch(() => ({ data: { contacts: [] } })),
      ]);
      setScopeEntities({
        dossiers: Array.isArray(d.data) ? d.data : d.data.dossiers || [],
        organizations: Array.isArray(o.data) ? o.data : o.data.organizations || [],
        contacts: Array.isArray(c.data) ? c.data : c.data.contacts || [],
      });
    } catch {}
  };

  const handleScopeChange = (newScope, newScopeId) => {
    setScope(newScope);
    setScopeId(newScopeId);
    scopeRef.current = newScope;
    scopeIdRef.current = newScopeId;
    handleNewConversation();
  };

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        sendAudio(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      toast.error('Impossible d\'acceder au microphone');
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, [isRecording]);

  const sendAudio = async (audioBlob) => {
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('session_id', sessionIdRef.current);

      // Pass scope params if selected (use refs to avoid stale closure)
      if (scopeRef.current && scopeIdRef.current) {
        formData.append('scope', scopeRef.current);
        formData.append('scope_id', scopeIdRef.current);
      }

      // Pass selected language for Whisper + LLM response
      formData.append('language', voiceLangRef.current || 'auto');

      const res = await axios.post(`${API}/persons/${personId}/voice-chat`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });

      const { user_text, assistant_text, audio } = res.data;

      setMessages(prev => [
        ...prev,
        { role: 'user', text: user_text },
        { role: 'assistant', text: assistant_text }
      ]);

      if (audio) {
        playAudio(audio);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erreur de conversation vocale';
      toast.error(detail);
    } finally {
      setIsProcessing(false);
    }
  };

  const playAudio = (base64Audio) => {
    try {
      const audioBytes = Uint8Array.from(atob(base64Audio), c => c.charCodeAt(0));
      const blob = new Blob([audioBytes], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      setIsPlaying(true);
      audio.onended = () => {
        setIsPlaying(false);
        URL.revokeObjectURL(url);
        audioRef.current = null;
      };
      audio.play();
    } catch {
      setIsPlaying(false);
    }
  };

  const handleNewConversation = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    sessionIdRef.current = crypto.randomUUID();
    setMessages([]);
    setIsPlaying(false);
    toast.success('Nouvelle conversation vocale');
  };

  const initials = person?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  const isSharedClone = person && person.linked_user_id !== user?.id && user?.role !== 'owner' && user?.role !== 'admin';

  // Scope label for display
  const getScopeLabel = () => {
    if (!scope || !scopeId) return null;
    if (scope === 'dossier') {
      const d = scopeEntities.dossiers.find(x => x.id === scopeId);
      return d ? d.title : scopeId;
    }
    if (scope === 'organization') {
      const o = scopeEntities.organizations.find(x => x.id === scopeId);
      return o ? o.name : scopeId;
    }
    if (scope === 'contact') {
      const c = scopeEntities.contacts.find(x => x.id === scopeId);
      return c ? `${c.first_name} ${c.last_name}` : scopeId;
    }
    return null;
  };

  return (
    <DashboardLayout title={`Parler avec ${person?.name || '...'}`}>
      <div className="flex flex-col h-[calc(100vh-5rem)]" data-testid="voice-chat-page">
        {/* Shared clone banner */}
        {isSharedClone && (
          <div className="flex items-center gap-2 px-4 py-2 border-b bg-blue-500/10 border-blue-500/20 text-sm text-blue-400" data-testid="shared-clone-banner">
            <User className="w-4 h-4 shrink-0" />
            <span>Vous parlez au clone de <strong>{person?.name}</strong></span>
          </div>
        )}
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/clones')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
              {initials}
            </div>
            <div>
              <h2 className="font-semibold text-base">{person?.name}</h2>
              <p className="text-xs text-muted-foreground">{person?.job_title}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleNewConversation} className="gap-1.5" data-testid="new-voice-conv-btn">
              <RotateCcw className="w-4 h-4" />
              Nouveau
            </Button>
            <Badge variant="outline" className="gap-1">
              <Phone className="w-3 h-3" />
              Vocal
            </Badge>
          </div>
        </div>

        {/* Scope Selector */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b bg-muted/30 flex-wrap" data-testid="voice-scope-selector">
          <span className="text-xs text-muted-foreground mr-1">Scope :</span>
          <Button
            variant={!scope ? 'default' : 'outline'}
            size="sm"
            className="h-7 text-xs gap-1"
            onClick={() => handleScopeChange(null, null)}
            data-testid="voice-scope-clone"
          >
            <Brain className="w-3 h-3" /> Clone
          </Button>
          <AsyncScopeCombobox
            token={token}
            endpoint="/dossiers"
            extractItems={(data) => Array.isArray(data) ? data : data.dossiers || []}
            value={scope === 'dossier' ? scopeId : null}
            onSelect={(id) => id ? handleScopeChange('dossier', id) : handleScopeChange(null, null)}
            placeholder="Dossier..."
            emptyText="Aucun dossier."
            labelFn={(d) => d.title || d.name || d.id}
            icon={FolderKanban}
            limitParam="limit"
            limitValue={10}
            testId="voice-scope-dossier-select"
          />
          <AsyncScopeCombobox
            token={token}
            endpoint="/organizations"
            extractItems={(data) => Array.isArray(data) ? data : data.organizations || []}
            value={scope === 'organization' ? scopeId : null}
            onSelect={(id) => id ? handleScopeChange('organization', id) : handleScopeChange(null, null)}
            placeholder="Organisation..."
            emptyText="Aucune organisation."
            labelFn={(o) => o.name}
            icon={Building}
            testId="voice-scope-org-select"
          />
          <AsyncScopeCombobox
            token={token}
            endpoint="/contacts"
            extractItems={(data) => data.contacts || (Array.isArray(data) ? data : [])}
            value={scope === 'contact' ? scopeId : null}
            onSelect={(id) => id ? handleScopeChange('contact', id) : handleScopeChange(null, null)}
            placeholder="Contact..."
            emptyText="Aucun contact."
            labelFn={(c) => `${c.first_name || ''} ${c.last_name || ''}`.trim()}
            icon={User}
            testId="voice-scope-contact-select"
          />
          {scope && (
            <Badge variant="outline" className="text-xs h-7 gap-1 border-primary/30 text-primary">
              {scope === 'dossier' && <FolderKanban className="w-3 h-3" />}
              {scope === 'organization' && <Building className="w-3 h-3" />}
              {scope === 'contact' && <User className="w-3 h-3" />}
              {getScopeLabel() || 'Filtre actif'}
            </Badge>
          )}

          {/* Language Selector */}
          <div className="ml-auto">
            <LanguageCombobox
              value={voiceLang}
              onSelect={handleLangChange}
              testId="voice-language-select"
            />
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-6" data-testid="voice-messages">
          {messages.length === 0 && !isProcessing && (
            <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground gap-4">
              <div className="w-20 h-20 rounded-full bg-primary/5 flex items-center justify-center">
                <Mic className="w-8 h-8 text-primary/40" />
              </div>
              <div>
                <p className="font-medium text-sm">Parlez avec {person?.name || 'le clone'}</p>
                <p className="text-xs mt-1">Appuyez sur le micro et posez votre question</p>
                {scope && (
                  <p className="text-xs mt-1 text-primary">
                    Scope : {scope === 'dossier' ? 'Dossier' : scope === 'organization' ? 'Organisation' : 'Contact'} — {getScopeLabel()}
                  </p>
                )}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <VoiceBubble
              key={i}
              role={msg.role}
              text={msg.text}
              isPlaying={isPlaying && msg.role === 'assistant' && i === messages.length - 1}
            />
          ))}

          {isProcessing && (
            <div className="flex justify-start mb-4">
              <div className="bg-muted px-4 py-3 rounded-2xl rounded-bl-md">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Transcription et reponse...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Mic Button */}
        <div className="flex flex-col items-center gap-3 py-6 border-t bg-background" data-testid="voice-controls">
          {!hasVoiceAccess ? (
            <div className="text-center space-y-3 py-4">
              <div className="w-20 h-20 mx-auto rounded-full bg-muted flex items-center justify-center">
                <Mic className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground">L'option Voix IA n'est pas activee pour votre equipe.</p>
              <Button onClick={() => setUpsellOpen(true)} data-testid="voice-gate-upsell-btn">
                {user?.role === 'owner' ? 'Activer la Voix IA' : 'En savoir plus'}
              </Button>
            </div>
          ) : (
            <>
              {isRecording && (
                <div className="flex items-center gap-2 text-sm text-red-500 animate-pulse">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  Enregistrement en cours...
                </div>
              )}

              <button
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isProcessing}
                className={`w-20 h-20 rounded-full flex items-center justify-center transition-all duration-200 shadow-lg ${
                  isProcessing
                    ? 'bg-muted text-muted-foreground cursor-not-allowed'
                    : isRecording
                      ? 'bg-red-500 hover:bg-red-600 text-white scale-110 ring-4 ring-red-500/30'
                      : 'bg-primary hover:bg-primary/90 text-primary-foreground hover:scale-105'
                }`}
                data-testid="mic-button"
              >
                {isProcessing ? (
                  <Loader2 className="w-8 h-8 animate-spin" />
                ) : isRecording ? (
                  <MicOff className="w-8 h-8" />
                ) : (
                  <Mic className="w-8 h-8" />
                )}
              </button>

              <p className="text-xs text-muted-foreground">
                {isProcessing ? 'Traitement...' : isRecording ? 'Appuyez pour envoyer' : 'Appuyez pour parler'}
              </p>
            </>
          )}
        </div>
      </div>

      <VoiceUpsellDialog
        open={upsellOpen}
        onOpenChange={setUpsellOpen}
        requiredFeature="voice_standard"
      />
    </DashboardLayout>
  );
}
